import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { I18n } from 'react-redux-i18n';
import styled from 'styled-components';
import get from 'lodash/get';
import { default as lodashfp } from 'lodash/fp';
import moment from 'moment';

import Header from 'components/Header';
import Kpi from 'components/Kpi';
import Carousel from 'components/Carousel';
import TabNavigation from 'components/TabNavigation';
import CountryLabel from 'components/CountryLabel';
import WarningMessageSmallScreen from 'components/WarningMessageSmallScreen';
import DistributionFilters from './components/DistributionFilters';
import Survey from './components/Survey';
import CategoryToggle from './components/CategoryToggle';
import history from '../../history';

import { useUserMe } from 'apiTyped/hooks/users.query';
import { useDistributionData } from 'apiTyped/hooks/distribution.query';
import getAllUrlParams from 'utils/getUrlParams';
import {
  showDistributionVintage,
  isSpiritOnly,
  showNCBars
} from 'utils/personalisation';

// Import page components
import DistributionKpiPage from './pages/Kpi';
import DistributionPosPage from './pages/PointsOfSales';
import DistributionPricePage from './pages/Price';
import DistributionVintagesPage from './pages/Vintages';
import DistributionRawDataPage from './pages/RawData';

const DistributionContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 20px;
  padding: 20px;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    gap: 16px;
    padding: 16px;
  }

  .filters,
  .navigation {
    margin-top: 20px;
  }

  .navigation {
    margin-bottom: 20px;
  }

  .headerContainer {
    margin-top: 22px;
    display: flex;
    flex-direction: row;
    align-items: center;

    @media (max-width: 1200px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .rightFeedContainer {
    display: flex;
    flex-direction: column;
    width: 100%;
    background-color: white;
  }

  .feedClientWines {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-top: 15px;
    margin-bottom: -10px;
    margin-left: 10px;
    z-index: 2;
  }

  .feedClientWines p {
    font-size: 12px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};
    font-weight: 600;
  }
`;

const Distribution = () => {
  // Use the new query hook instead of Redux action
  const { isLoadingDistribution, distributionData } = useDistributionData();
  const { isLoading: isLoadingUser, user } = useUserMe();
  const isBranded = user?.isBranded;
  const clientTags = user?.client.tags || [];
  const displayWineshops = user?.client?.displayWineshops || false;

  // Track if we've restored filters from localStorage
  const hasRestoredFilters = React.useRef(false);

  // Determine wine/spirit toggle visibility
  const isSpiritOnlyFlag = useMemo(
    () => isSpiritOnly(clientTags),
    [clientTags]
  );
  const hasSpirit = useMemo(() => showNCBars(clientTags), [clientTags]);
  const shouldShowCategoryToggle = hasSpirit && !isSpiritOnlyFlag;

  const [localState, setLocalState] = useState({
    alternative: 1,
    currentMarket: '',
    // Form state moved from Redux
    wine: [],
    competitors: [],
    market: null,
    year: null,
    currentNetworkCode: null,
    currentCampaign: null,
    currentUSCampaign: false,
    // Default to Spirit ('spirit') if spirit-only, otherwise Wine ('wine')
    wineType: isSpiritOnlyFlag ? 'spirit' : 'wine'
  });

  // Helper function to update local form state
  const updateLocalForm = useCallback((updates) => {
    setLocalState((prevState) => {
      const newState = {
        ...prevState,
        ...updates
      };

      // Save relevant filters to localStorage (only serializable data)
      const filtersToSave = {
        wine: newState.wine,
        competitors: newState.competitors,
        // Only save primitive values, not React elements
        marketId: newState.market?.value || null,
        yearValue: newState.year?.value || null,
        yearMonth: newState.year?.month || null,
        currentNetworkCode: newState.currentNetworkCode,
        currentUSCampaign: newState.currentUSCampaign,
        wineType: newState.wineType
      };

      try {
        localStorage.setItem(
          'distribution_filters',
          JSON.stringify(filtersToSave)
        );
      } catch (e) {
        console.error('Error saving filters to localStorage:', e);
      }

      return newState;
    });
  }, []);

  const filteringCampaignProductTypeField = useMemo(
    () => (localState.wineType == 'spirit' ? 'spirit_survey' : 'wine_survey'),
    [localState.wineType]
  );

  const distributionDataCampaigns = useMemo(() => {
    const distributionDataAllCampaigns = distributionData.campaigns || [];
    // we're only allowed to choose from the correct US/non-US set
    const chooseUSCampaign = localState.currentUSCampaign;
    return distributionDataAllCampaigns
      .filter((item) =>
        chooseUSCampaign
          ? item.market.human_validation === false
          : item.market.human_validation === true
      )
      .filter((item) => item[filteringCampaignProductTypeField]);
  }, [
    distributionData.campaigns,
    localState.currentUSCampaign,
    filteringCampaignProductTypeField
  ]);

  // Initialize component data and handle displayWineshops configuration
  useEffect(() => {
    window.scrollTo(0, 0);
    // Data is now automatically fetched by the useDistributionData hook
    if (displayWineshops) {
      setLocalState((prevState) => ({
        ...prevState,
        alternative: Math.random() >= 0.5 ? 0 : 1
      }));
    }
  }, [displayWineshops]);

  // Restore filters from localStorage on component mount
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem('distribution_filters');
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);

        // Restore simple values that don't need reconstruction
        const updates = {};

        if (parsed.wine && parsed.wine.length > 0) {
          updates.wine = parsed.wine;
          hasRestoredFilters.current = true;
        }
        if (parsed.competitors) updates.competitors = parsed.competitors;
        if (parsed.currentNetworkCode)
          updates.currentNetworkCode = parsed.currentNetworkCode;
        if (typeof parsed.currentUSCampaign !== 'undefined')
          updates.currentUSCampaign = parsed.currentUSCampaign;
        if (parsed.wineType) updates.wineType = parsed.wineType;

        // Store market and year IDs for later reconstruction
        if (parsed.marketId) updates._savedMarketId = parsed.marketId;
        if (parsed.yearValue) updates._savedYearValue = parsed.yearValue;
        if (parsed.yearMonth) updates._savedYearMonth = parsed.yearMonth;

        // Apply updates if we have any
        if (Object.keys(updates).length > 0) {
          setLocalState((prevState) => ({
            ...prevState,
            ...updates
          }));
        }
      }
    } catch (e) {
      console.error('Error restoring filters from localStorage:', e);
    }
  }, []);

  // Reconstruct market and year objects from saved IDs once campaigns are loaded
  useEffect(() => {
    if (!distributionDataCampaigns || distributionDataCampaigns.length === 0)
      return;
    if (!localState._savedMarketId && !localState._savedYearValue) return;

    const updates = {};

    // Reconstruct market object
    if (localState._savedMarketId) {
      const matchingCampaign = distributionDataCampaigns.find(
        (campaign) => campaign.market.id === localState._savedMarketId
      );

      if (matchingCampaign) {
        updates.market = {
          value: matchingCampaign.market.id,
          name: matchingCampaign.market.name,
          code: matchingCampaign.market.country
            ? matchingCampaign.market.country.code
            : null,
          label: matchingCampaign.market.country ? (
            <CountryLabel
              city={matchingCampaign.market.name}
              name={matchingCampaign.market.country.code}
            />
          ) : (
            matchingCampaign.market.name
          )
        };
      }
      updates._savedMarketId = undefined; // Clear the temp value
    }

    // Reconstruct year object
    if (localState._savedYearValue && localState._savedYearMonth) {
      const matchingCampaign = distributionDataCampaigns.find(
        (campaign) =>
          campaign.year === localState._savedYearValue &&
          campaign.month === localState._savedYearMonth
      );

      if (matchingCampaign) {
        updates.year = {
          value: matchingCampaign.year,
          month: matchingCampaign.month,
          label: matchingCampaign.continuous_mode
            ? I18n.t('restaurants&caves.nowVision')
            : moment(matchingCampaign.month, 'M').format('MMMM') +
              ' ' +
              matchingCampaign.year
        };
      }
      updates._savedYearValue = undefined;
      updates._savedYearMonth = undefined;
    }

    if (Object.keys(updates).length > 0) {
      setLocalState((prevState) => ({
        ...prevState,
        ...updates
      }));
    }
  }, [
    distributionDataCampaigns,
    localState._savedMarketId,
    localState._savedYearValue
  ]);

  useEffect(() => {
    if (distributionDataCampaigns && distributionDataCampaigns.length > 0) {
      // Skip if we're about to restore a saved market
      if (localState._savedMarketId) return;

      // Get the first market:
      updateLocalForm({
        currentCampaign: null,
        market: distributionDataCampaigns[0].market.id
      });
    }
  }, [distributionDataCampaigns]);

  // Memoize client_wines and client_marques based on human_validation
  const { client_wines, client_marques } = useMemo(() => {
    const {
      client_wines_distri = [],
      client_wines_gdv = [],
      client_marques_distri = [],
      client_marques_gdv = []
    } = distributionData;

    const currentCampaign = localState?.currentCampaign;
    const isHumanValidation =
      currentCampaign?.market?.human_validation === true;

    const clientWines = isHumanValidation
      ? client_wines_distri
      : client_wines_gdv;
    const filteredWines = clientWines.filter((product) => {
      // Fortified wines are treated as wines
      if (localState.wineType === 'wine') {
        return (
          product.product_type === 'wine' ||
          product.product_type === 'fortified wine'
        );
      }
      return product.product_type == localState.wineType;
    });
    const clientMarques = isHumanValidation
      ? client_marques_distri
      : client_marques_gdv;
    const filteredMarques = clientMarques.filter((marque) => {
      // Fortified wines are treated as wines
      if (localState.wineType === 'wine') {
        return (
          marque.product_type === 'wine' ||
          marque.product_type === 'fortified wine'
        );
      }
      return marque.product_type == localState.wineType;
    });
    return {
      client_wines: filteredWines,
      client_marques: filteredMarques
    };
  }, [
    distributionData.client_wines_distri,
    distributionData.client_wines_gdv,
    distributionData.client_marques_distri,
    distributionData.client_marques_gdv,
    localState?.currentCampaign?.market?.human_validation,
    localState.wineType
  ]);

  const hasUSCampaigns = useMemo(() => {
    // true if any of the campaigns are US-based
    if (!distributionData.campaigns) return false;
    return distributionData.campaigns.some(
      (campaign) => campaign.market.human_validation === false
    );
  }, [distributionData.campaigns]);

  const isOnlyUS = useMemo(() => {
    if (!distributionData.campaigns || distributionData.campaigns.length === 0)
      return false;
    return distributionData.campaigns.every(
      (campaign) => campaign.market.human_validation === false
    );
  }, [distributionData.campaigns]);

  useEffect(() => {
    if (isOnlyUS) {
      updateLocalForm({ currentUSCampaign: true });
    }
  }, [isOnlyUS]);

  // Function to get rivals
  const getRivals = useCallback(
    (wine) => {
      let rivals = [];
      if (!wine || !wine.length) {
        return [];
      } else {
        const data = isBranded ? [...client_marques] : [...client_wines];
        wine = wine.map((item) => item.value);
        rivals = data
          .filter((item) =>
            wine.includes(isBranded ? item.marque_name : item.wine_name)
          )
          .map((item) => item.rivals);
        if (rivals.length > 0) {
          rivals = [].concat.apply([], rivals);
        }
        return [...new Set(rivals.filter((item) => !!item))];
      }
    },
    [isBranded, client_wines, client_marques]
  );

  // Handle initial wine selection when data loads
  // This will be triggered when we move from GDV to normal campaigns
  // since client_marques / client_wines will change
  useEffect(() => {
    // Skip if we've already restored filters from localStorage
    if (hasRestoredFilters.current) {
      return;
    }

    const hasData = isBranded
      ? client_marques && client_marques.length > 0
      : client_wines && client_wines.length > 0;
    if (!hasData) {
      updateLocalForm({
        wine: [],
        competitors: []
      });
      return;
    }

    const firstWine = {
      label: get(client_wines, '[0].wine_name'),
      value: get(client_wines, '[0].wine_name')
    };
    const firstBrand = {
      label: get(client_marques, '[0].marque_name'),
      value: get(client_marques, '[0].marque_name')
    };

    const selectedItem = isBranded ? firstBrand : firstWine;

    // Only update if there's actually a valid wine/brand to select
    if (!selectedItem.value) return;

    // Calculate competitors inline to avoid dependency issues
    let competitors = [];
    const data = isBranded ? [...client_marques] : [...client_wines];
    const wineValues = [selectedItem.value];
    const rivals = data
      .filter((item) =>
        wineValues.includes(isBranded ? item.marque_name : item.wine_name)
      )
      .map((item) => item.rivals);
    if (rivals.length > 0) {
      const flatRivals = [].concat.apply([], rivals);
      competitors = [...new Set(flatRivals.filter((item) => !!item))];
    }

    updateLocalForm({
      wine: [selectedItem],
      competitors: competitors
    });
  }, [client_wines, client_marques, isBranded]);

  // Memoize findCampaign to prevent unnecessary re-renders
  const findCampaign = useCallback(() => {
    const { market, year, currentCampaign, currentNetworkCode } = localState;
    const isCampaignValid =
      currentCampaign &&
      (currentCampaign.id === -1 ||
        currentCampaign[filteringCampaignProductTypeField]);

    let filteredCampaigns = distributionDataCampaigns;
    let targetCampaign;
    let targetMarket;
    if (!isCampaignValid && distributionDataCampaigns.length > 0) {
      // Parse search params to find campaign or market
      const options = getAllUrlParams(window.location.href);
      if ('campaignId' in options) {
        filteredCampaigns = filteredCampaigns.find(
          (item) => get(item, 'id') === parseInt(options.campaignId)
        );
      } else if ('market' in options) {
        filteredCampaigns = filteredCampaigns.find(
          (item) =>
            get(item, 'market.name').toLowerCase() ===
            options.market.toLowerCase()
        );
      }

      if (!filteredCampaigns || !get(filteredCampaigns, 'market.id')) {
        targetCampaign = distributionDataCampaigns[0];
        targetMarket = {
          value: get(targetCampaign, 'market.id'),
          label: get(targetCampaign, 'market.country') ? (
            <CountryLabel
              city={get(targetCampaign, 'market.name')}
              name={get(targetCampaign, 'market.country.code')}
            />
          ) : (
            get(targetCampaign, 'market.name')
          )
        };
      }

      updateLocalForm({
        currentCampaign: targetCampaign,
        currentNetworkCode: targetCampaign.network.code,
        market: targetMarket,
        year: {
          value: targetCampaign.year,
          month: targetCampaign.month,
          label: targetCampaign.continuous_mode
            ? I18n.t('restaurants&caves.nowVision')
            : moment(targetCampaign.month, 'M').format('MMMM') +
              ' ' +
              targetCampaign.year
        }
      });

      // Fin de la première partie
    } else {
      filteredCampaigns = filteredCampaigns.filter((item) =>
        market ? item.market.id === market.value : false
      );

      const networks = [
        ...new Set(
          filteredCampaigns.map((item) => item.network && item.network.code)
        )
      ];
      if (networks.length > 1) {
        filteredCampaigns = filteredCampaigns.filter(
          (item) => item.network.code === currentNetworkCode
        );
        filteredCampaigns = filteredCampaigns.filter((item) =>
          year ? item.year === year.value && item.month === year.month : false
        );
      } else if (networks && networks.length > 0) {
        let yearOptions = [];
        filteredCampaigns
          .sort(function (a, b) {
            return String(a.year).localeCompare(String(b.year));
          })
          .forEach((item) => {
            const label = item.continuous_mode
              ? I18n.t('restaurants&caves.nowVision')
              : moment(item.month, 'M').format('MMMM') + ' ' + item.year;

            if (
              !yearOptions.map((yearOption) => yearOption.label).includes(label)
            ) {
              yearOptions.push({
                value: item.year,
                month: item.month,
                label: label
              });
            }
          });
        updateLocalForm({ currentNetworkCode: networks[0] });
        if (
          !year ||
          !yearOptions.map((item) => item.label).includes(year.label)
        ) {
          // Find the best matching year or default to the latest
          const bestYear =
            yearOptions.find(
              (option) =>
                year &&
                option.value === year.value &&
                option.month === year.month
            ) || yearOptions[yearOptions.length - 1];

          // Only update if we have a valid year and it's different from current
          if (bestYear && (!year || bestYear.label !== year.label)) {
            updateLocalForm({
              year: bestYear
            });
            filteredCampaigns = filteredCampaigns.filter(
              (item) =>
                item.year === bestYear.value && item.month === bestYear.month
            );
          } else if (year) {
            filteredCampaigns = filteredCampaigns.filter(
              (item) => item.year === year.value && item.month === year.month
            );
          }
        } else {
          filteredCampaigns = filteredCampaigns.filter(
            (item) => item.year === year.value && item.month === year.month
          );
        }
      }

      if (filteredCampaigns && filteredCampaigns.length > 0) {
        targetCampaign = filteredCampaigns[0];
        if (!currentCampaign || currentCampaign.id !== targetCampaign.id) {
          updateLocalForm({
            currentCampaign: targetCampaign
          });
        }
      } else {
        if (!currentCampaign || currentCampaign.id !== -1) {
          updateLocalForm({
            currentCampaign: { id: -1 }
          });
        }
      }
    }
  }, [
    distributionDataCampaigns,
    filteringCampaignProductTypeField,
    localState.market?.value,
    localState.year?.value,
    localState.year?.month,
    localState.currentCampaign?.id,
    localState.currentNetworkCode,
    updateLocalForm
  ]);

  // Handle campaign updates when data or form changes
  useEffect(() => {
    if (isLoadingUser | isLoadingDistribution) return;
    if (distributionData.campaigns && distributionData.campaigns.length > 0) {
      findCampaign();
    }
  }, [
    distributionData.campaigns,
    localState.market?.value,
    localState.year?.value,
    localState.year?.month,
    localState.currentNetworkCode,
    localState.wineType
  ]);
  // Function to filter since start year and exclude 9998/9999 years
  const filterSinceStartYear = useCallback((data) => {
    return data.filter((item) => {
      // Filter out items with year 9998 or 9999
      if (item.year === 9998 || item.year === 9999) return false;

      return moment(new Date(item.year, item.month - 1, 1, 1, 1, 1)).isAfter(
        moment().startOf('year')
      );
    });
  }, []);

  // Function to sum dn - use form state directly
  const sumDn = useCallback(() => {
    const { alternative } = localState;
    const { kpis = [] } = distributionData;
    return alternative === 1 ? kpis.dn_on : kpis.dn_off;
  }, [localState.alternative, distributionData]);

  // Memoize options calculation to prevent unnecessary recalculations
  const getOptions = useMemo(() => {
    let { market, currentNetworkCode } = localState;

    const marketOptions = lodashfp.flow(
      lodashfp.filter((campaign) => campaign.market),
      lodashfp.uniqBy('market.id'),
      lodashfp.map((campaign) => ({
        value: campaign.market.id,
        name: campaign.market.name,
        code: campaign.market.country ? campaign.market.country.code : null,
        label: campaign.market.country ? (
          <CountryLabel
            city={campaign.market.name}
            name={campaign.market.country.code}
          />
        ) : (
          campaign.market.name
        )
      }))
    )(distributionDataCampaigns);

    const yearOptions = lodashfp.flow(
      lodashfp.filter((item) =>
        market ? item.market && item.market.id === market.value : true
      ),
      lodashfp.filter((item) => item.network.code === currentNetworkCode),
      lodashfp.sortBy((item) => item.year * 100 + item.month),
      lodashfp.map((item) => ({
        value: item.year,
        month: item.month,
        label: item.continuous_mode
          ? I18n.t('restaurants&caves.nowVision')
          : moment(item.month, 'M').format('MMMM') + ' ' + item.year
      })),
      lodashfp.uniqBy('label')
    )(distributionDataCampaigns);

    const networkOptions = lodashfp.flow(
      lodashfp.filter((item) =>
        market ? item.market && item.market.id === market.value : true
      ),
      lodashfp.map((item) => item.network.code),
      lodashfp.uniq
    )(distributionDataCampaigns);

    const wineOptions = isBranded
      ? [
          ...client_marques.map((item) => ({
            value: item.marque_name,
            label: item.marque_name
          }))
        ]
      : [
          ...client_wines.map((item) => ({
            value: item.wine_name,
            label: item.wine_name
          }))
        ];

    return {
      year: yearOptions,
      market: marketOptions,
      wine: wineOptions,
      disabledRadio: networkOptions.length < 2
    };
  }, [
    distributionDataCampaigns,
    client_wines,
    client_marques,
    localState.market?.value,
    localState.currentNetworkCode,
    localState.year?.value,
    localState.year?.month,
    isBranded
  ]);

  // Separate effect to handle form validation and updates
  useEffect(() => {
    if (!getOptions || !localState) return;

    const networkOptions = lodashfp.flow(
      lodashfp.filter((item) =>
        localState.market
          ? item.market && item.market.id === localState.market.value
          : true
      ),
      lodashfp.map((item) => item.network.code),
      lodashfp.uniq
    )(distributionDataCampaigns);

    // Update currentNetworkCode if current one is not available
    if (
      networkOptions.length > 1 &&
      !networkOptions.includes(localState.currentNetworkCode)
    ) {
      updateLocalForm({
        currentNetworkCode: networkOptions[0]
      });
      return; // Exit early to prevent multiple updates
    }

    // Only update year if there are available options and current year is invalid
    if (
      localState.year &&
      getOptions.year.length > 0 &&
      !getOptions.year.map((item) => item.label).includes(localState.year.label)
    ) {
      // Find the best matching year instead of always picking the last one
      const bestYear =
        getOptions.year.find(
          (option) =>
            option.value === localState.year.value &&
            option.month === localState.year.month
        ) || getOptions.year[getOptions.year.length - 1];

      // Only update if the year is actually different and we haven't just updated it
      if (bestYear.label !== localState.year.label) {
        updateLocalForm({
          year: bestYear
        });
      }
    }
  }, [
    distributionDataCampaigns,
    getOptions?.year?.length,
    localState.market?.value,
    localState.currentNetworkCode,
    localState.year?.label
  ]);

  const handleChangeMarket = useCallback(
    (value) => {
      updateLocalForm({
        market: value
      });
    },
    [updateLocalForm]
  );

  const handleChangeYear = useCallback(
    (value) => {
      updateLocalForm({ year: value });
    },
    [updateLocalForm]
  );

  const handleChangeWine = useCallback(
    (value, options) => {
      if (value && value.map((item) => item.value).includes('all')) {
        const competitors = getRivals(options);
        updateLocalForm({ wine: options, competitors });
      } else {
        const competitors = getRivals(value);
        updateLocalForm({ wine: value, competitors });
      }
    },
    [getRivals, updateLocalForm]
  );

  const handleChangeUSCampaign = useCallback(
    (value) => {
      updateLocalForm({ currentUSCampaign: value });
    },
    [updateLocalForm]
  );

  const handleChangeWineType = useCallback(
    (value) => {
      updateLocalForm({ wineType: value });
      // If switching to spirit and currently on price page, redirect to KPI page
      if (
        value === 'spirit' &&
        history.location.pathname === '/distribution/restaurants/price'
      ) {
        history.push('/distribution/restaurants');
      }
    },
    [updateLocalForm]
  );

  // Function to get navigation
  const getNavigation = useCallback(() => {
    const isSpirit = localState.wineType === 'spirit';
    return [
      {
        label: I18n.t('restaurants&caves.navigationOptions.kpi'),
        route: '/distribution/restaurants'
      },
      {
        label: I18n.t('restaurants&caves.navigationOptions.pos'),
        route: '/distribution/restaurants/points-of-sale'
      },
      !isSpirit && {
        label: I18n.t('restaurants&caves.navigationOptions.price'),
        route: '/distribution/restaurants/price'
      },
      showDistributionVintage(clientTags) && {
        label: I18n.t('restaurants&caves.navigationOptions.vintage'),
        route: '/distribution/restaurants/vintages'
      },
      {
        label: I18n.t('restaurants&caves.navigationOptions.raw'),
        route: '/distribution/restaurants/raw-data'
      }
    ].filter((item) => item !== false);
  }, [clientTags, localState.wineType]);

  // Function to render filters - stabilized
  const renderFilters = useCallback(() => {
    const {
      market,
      wine,
      competitors,
      year,
      currentNetworkCode,
      currentCampaign,
      currentUSCampaign,
      wineType
    } = localState;

    const options = getOptions;

    const networksOptions = [
      {
        label: I18n.t(
          `restaurants&caves.networkOptions.${localState.wineType}.restaurant`
        ),
        value: 'ON'
      },
      {
        label: I18n.t(
          `restaurants&caves.networkOptions.${localState.wineType}.cave`
        ),
        value: 'OFF'
      }
    ];

    let presentationPath;
    if (currentCampaign && currentCampaign.market_presentations) {
      const targetPresentationField =
        wineType === 'spirit'
          ? 'spirit_presentation_path'
          : 'wine_presentation_path';
      presentationPath =
        currentCampaign.market_presentations[targetPresentationField];
    }
    return (
      <div className="filters">
        <DistributionFilters
          currentCampaign={currentCampaign}
          marketFilter={{
            selectedOption: market,
            options: options.market,
            onChange: (selectedOption) =>
              selectedOption && handleChangeMarket(selectedOption),
            label: I18n.t('restaurants&caves.filters.market'),
            placeholder: I18n.t('restaurants&caves.filters.marketPlaceholder')
          }}
          yearFilter={{
            selectedOption: year,
            options: options.year,
            onChange: (selectedOption) => handleChangeYear(selectedOption),
            label: I18n.t('restaurants&caves.filters.date'),
            placeholder: I18n.t('restaurants&caves.filters.datePlaceholder')
          }}
          USCampaignFilter={{
            checked: currentUSCampaign,
            onChange: (selectedOption) =>
              handleChangeUSCampaign(selectedOption),
            label: I18n.t('restaurants&caves.filters.USmarket'),
            disabledSwitch: !hasUSCampaigns || isOnlyUS
            // disabledSwitch: !hasUSCampaigns
          }}
          wineFilter={{
            selectedOption: wine,
            options: [
              ...options.wine,
              { label: I18n.t('placeholderAll'), value: 'all' }
            ],
            onChange: (selectedOption) =>
              handleChangeWine(selectedOption, options.wine),
            label: isBranded
              ? I18n.t('restaurants&caves.filters.brands')
              : I18n.t('restaurants&caves.filters.wines'),
            placeholder: isBranded
              ? I18n.t('restaurants&caves.filters.brandsPlaceholder')
              : I18n.t('restaurants&caves.filters.winesPlaceholder')
          }}
          competitorsFilter={{
            value: `${competitors.length} ${I18n.t(
              'restaurants&caves.filters.rivals'
            )}`,
            allOption: true,
            data: getRivals(wine),
            selected: competitors,
            onChange: (labels) => {
              updateLocalForm({ competitors: labels });
            }
          }}
          radioButtonsProps={{
            radioButtons: networksOptions.map((item) => ({
              ...item,
              checked: item.value === currentNetworkCode
            })),
            label: I18n.t('restaurants&caves.filters.network'),
            disabled: options.disabledRadio,
            returnValue: true,
            onChange: (value) => {
              updateLocalForm({
                currentNetworkCode: value
              });
            }
          }}
          changeInputValueMarket={(value) =>
            setLocalState((prevState) => ({
              ...prevState,
              currentMarket: value
            }))
          }
          inputValueMarket={localState.currentMarket}
          marketPresentationPdf={presentationPath}
        />
      </div>
    );
  }, [localState, getOptions]);

  // Render the component
  const navigation = getNavigation();
  const dataYear = filterSinceStartYear(distributionDataCampaigns);
  const { alternative } = localState;
  const valueKpi = sumDn();

  return (
    <DistributionContainer>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <Header
          title={I18n.t(`restaurants&caves.title`)}
          subTitle={
            !displayWineshops
              ? I18n.t('restaurantsMaj')
              : I18n.t(`restaurants&caves.subTitle.${localState.wineType}`)
          }
        />
        {shouldShowCategoryToggle && (
          <CategoryToggle
            value={localState.wineType}
            onChange={handleChangeWineType}
          />
        )}
      </div>
      <div className="headerContainer">
        <Kpi
          className="kpi"
          title={
            alternative === 1
              ? I18n.t('restaurants&caves.kpiTextRestaurant')
              : I18n.t('restaurants&caves.kpiTextCave')
          }
          subTitle={I18n.t('restaurants&caves.kpiSubTitle')}
          value={valueKpi ? valueKpi : 0}
          handleNext={
            displayWineshops
              ? () =>
                  setLocalState((prevState) => ({
                    ...prevState,
                    alternative: localState.alternative === 1 ? 0 : 1
                  }))
              : undefined
          }
          tooltip={I18n.t('restaurants&caves.kpiTooltip')}
        />
        <Carousel ItemComponent={Survey} data={dataYear} />
      </div>
      {window.innerWidth > 845 ? (
        <React.Fragment>
          {renderFilters()}
          <div className="navigation">
            <TabNavigation buttons={navigation} />
          </div>
          {history.location.pathname === '/distribution/restaurants' && (
            <DistributionKpiPage form={localState} />
          )}
          {history.location.pathname ===
            '/distribution/restaurants/points-of-sale' && (
            <DistributionPosPage
              form={localState}
              isBranded={isBranded}
              clientTags={clientTags}
            />
          )}
          {history.location.pathname === '/distribution/restaurants/price' && (
            <DistributionPricePage
              client_marques={client_marques}
              client_wines={client_wines}
              form={localState}
              isBranded={isBranded}
            />
          )}
          {history.location.pathname ===
            '/distribution/restaurants/vintages' && (
            <DistributionVintagesPage
              form={localState}
              isBranded={isBranded}
              client_marques={client_marques}
            />
          )}
          {history.location.pathname ===
            '/distribution/restaurants/raw-data' && (
            <DistributionRawDataPage form={localState} isBranded={isBranded} />
          )}
        </React.Fragment>
      ) : (
        <WarningMessageSmallScreen />
      )}
    </DistributionContainer>
  );
};

export default Distribution;
